import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Webhook,
  Loader2,
  RefreshCw,
  Copy,
  Eye,
  EyeOff,
  RotateCcw,
  Power,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { fmtServerDateTime } from './utils/datetime';
import { useAdminOtpGuard } from './useAdminOtpGuard';

const fmtTime = (iso) => {
  if (!iso) return '—';
  return fmtServerDateTime(iso);
};

const STATUS_CLS = {
  accepted:  'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  duplicate: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  rejected:  'bg-red-500/15 text-red-300 border-red-500/30',
  error:     'bg-red-500/15 text-red-300 border-red-500/30',
};

export function AdminWebhooksPage() {
  const { admin, loading } = useAdminAuth();
  const [config, setConfig] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [fetching, setFetching] = useState(true);
  const [expanded, setExpanded] = useState(null);

  // Dual-channel (Email + WhatsApp) OTP guard (Part D).
  const { guard, dialog: otpDialog } = useAdminOtpGuard();

  const loadConfig = useCallback(async () => {
    try {
      const r = await adminApi.get('/admin/webhooks');
      setConfig(r.data);
    } catch {
      toast.error('Could not load webhook config');
    }
  }, []);

  const loadDeliveries = useCallback(async () => {
    setFetching(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '50' });
      if (statusFilter) params.set('status', statusFilter);
      if (search.trim()) params.set('search', search.trim());
      const r = await adminApi.get(`/admin/webhooks/deliveries?${params.toString()}`);
      setDeliveries(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch {
      toast.error('Could not load deliveries');
    } finally {
      setFetching(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => { if (admin) { loadConfig(); loadDeliveries(); } }, [admin, loadConfig, loadDeliveries]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error('Copy failed'); }
  };

  const rotateSecret = async () => {
    if (!window.confirm(
      'Rotate the webhook secret?\n\nYour website will stop accepting 401-unauthorized responses until you update its stored secret.',
    )) return;
    await guard({
      requestOtpUrl: '/admin/webhooks/request-otp',
      title: 'Confirm secret rotation',
      description: 'Rotating the webhook secret needs dual Email + WhatsApp OTP verification.',
      performSave: async (headers) => {
        setRotating(true);
        try {
          await adminApi.post('/admin/webhooks/rotate', null, { headers });
          toast.success('Webhook secret rotated');
          await loadConfig();
        } finally {
          setRotating(false);
        }
      },
    });
  };

  const toggleActive = async (next) => {
    await guard({
      requestOtpUrl: '/admin/webhooks/request-otp',
      title: `Confirm webhook ${next ? 'enable' : 'disable'}`,
      description: 'Toggling webhook status needs dual Email + WhatsApp OTP verification.',
      performSave: async (headers) => {
        setToggling(true);
        try {
          await adminApi.put('/admin/webhooks/active', { is_active: next }, { headers });
          toast.success(next ? 'Webhook enabled' : 'Webhook disabled');
          await loadConfig();
        } finally {
          setToggling(false);
        }
      },
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div
      data-testid="admin-webhooks-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800" data-testid="webhooks-back">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Webhook className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">Website Webhook</div>
            <div className="text-xs text-slate-400">
              Accept license purchases from your own website and auto-deliver the license email
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        {!config ? (
          <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-10 flex items-center gap-2 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Config card */}
            <section className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-5" data-testid="webhook-config-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-semibold">Endpoint</div>
                  <div className="text-xs text-slate-400">Configure this URL on your e-commerce checkout's webhook step</div>
                </div>
                <div className="flex items-center gap-3" data-testid="webhook-active-toggle">
                  <Power className={`w-4 h-4 ${config.is_active ? 'text-emerald-400' : 'text-slate-500'}`} />
                  <span className={`text-xs font-semibold uppercase tracking-wider ${config.is_active ? 'text-emerald-300' : 'text-slate-400'}`}>
                    {config.is_active ? 'Active' : 'Disabled'}
                  </span>
                  <Switch
                    checked={config.is_active}
                    disabled={toggling}
                    onCheckedChange={toggleActive}
                    data-testid="webhook-active-switch"
                  />
                </div>
              </div>

              <div className="rounded-lg bg-slate-950 border border-slate-800 p-3 flex items-center gap-2">
                <code className="font-mono text-sm text-amber-300 truncate flex-1" data-testid="webhook-url">{config.webhook_url}</code>
                <button onClick={() => copy(config.webhook_url, 'URL')} className="text-slate-400 hover:text-slate-200" data-testid="copy-webhook-url">
                  <Copy className="w-4 h-4" />
                </button>
              </div>

              {/* Secret */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-white font-semibold text-sm">HMAC Secret</div>
                    <div className="text-[11px] text-slate-500">Rotated {fmtTime(config.rotated_at) === '—' ? 'never' : `on ${fmtTime(config.rotated_at)}`}</div>
                  </div>
                  <Button
                    onClick={rotateSecret}
                    disabled={rotating}
                    variant="outline"
                    size="sm"
                    data-testid="webhook-rotate-btn"
                    className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
                  >
                    {rotating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                    Rotate
                  </Button>
                </div>
                <div className="rounded-lg bg-slate-950 border border-slate-800 p-3 flex items-center gap-2">
                  <code className="font-mono text-xs text-slate-300 truncate flex-1" data-testid="webhook-secret">
                    {showSecret ? config.secret : '•'.repeat(Math.min(64, (config.secret || '').length))}
                  </code>
                  <button onClick={() => setShowSecret((s) => !s)} className="text-slate-400 hover:text-slate-200" data-testid="webhook-secret-toggle">
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button onClick={() => copy(config.secret, 'Secret')} className="text-slate-400 hover:text-slate-200" data-testid="copy-webhook-secret">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Signature reference */}
              <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-4 text-xs text-amber-100/90 space-y-2">
                <div className="font-bold text-amber-300 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> How to sign requests from your website
                </div>
                <div>Include these two headers on every POST:</div>
                <ul className="font-mono text-[11px] list-disc pl-5 space-y-0.5 text-amber-200/90">
                  <li><b>X-Webhook-Timestamp:</b> current unix time in seconds</li>
                  <li><b>X-Webhook-Signature:</b> sha256=HMAC_SHA256(secret, raw_body + "." + timestamp)</li>
                </ul>
                <div className="text-amber-200/80">Requests older than 5 minutes are rejected (replay protection).</div>
              </div>

              {/* Payload shape */}
              <div>
                <div className="text-white font-semibold text-sm mb-2">Expected JSON body</div>
                <pre
                  data-testid="webhook-payload-sample"
                  className="bg-slate-950 border border-slate-800 rounded-md p-3 text-[11px] text-emerald-200 overflow-auto"
                >{`{
  "event_id": "unique-id-from-your-store",
  "order_id": "ORD-12345",
  "customer_email": "buyer@example.com",
  "customer_name": "Optional Name",
  "tier": "pro",
  "amount_paise": 199900,
  "metadata": { "source": "shop.example.com" }
}`}</pre>
                <div className="text-[11px] text-slate-500 mt-2">
                  Response: <code>200</code> with <code>{`{license_code, license_id, duplicate?}`}</code>.
                  Re-POST with the same <code>event_id</code> is safe — it returns the originally minted code.
                </div>
              </div>
            </section>

            {/* Deliveries */}
            <section className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-semibold">Recent Deliveries</div>
                  <div className="text-xs text-slate-400">Every inbound POST attempt — accepted, duplicate, or rejected</div>
                </div>
                <Button onClick={loadDeliveries} disabled={fetching} size="sm" variant="outline" data-testid="webhooks-refresh" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fetching ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1.5 text-xs text-slate-400 uppercase tracking-wider">
                  <Filter className="w-3.5 h-3.5" /> Filter
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  data-testid="webhook-status-filter"
                  className="bg-slate-800/50 border border-slate-700 text-white text-xs rounded-md px-2 py-1.5"
                >
                  <option value="">All statuses</option>
                  <option value="accepted">Accepted</option>
                  <option value="duplicate">Duplicate</option>
                  <option value="rejected">Rejected</option>
                </select>
                <Input
                  placeholder="event_id / order_id / reason…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); loadDeliveries(); } }}
                  data-testid="webhook-search"
                  className="bg-slate-800/50 border-slate-700 text-white text-sm h-9 max-w-xs"
                />
                <span className="text-xs text-slate-500 ml-auto">{total.toLocaleString('en-IN')} delivery events</span>
              </div>

              <div className="rounded-xl overflow-hidden border border-slate-800">
                {fetching && deliveries.length === 0 ? (
                  <div className="p-10 flex items-center justify-center gap-2 text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                  </div>
                ) : deliveries.length === 0 ? (
                  <div className="p-10 text-center text-slate-500 text-sm">No deliveries yet.</div>
                ) : (
                  <table className="w-full text-sm" data-testid="webhook-deliveries-table">
                    <thead className="bg-slate-950/60">
                      <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                        <th className="px-4 py-2 text-left w-44">Time</th>
                        <th className="px-4 py-2 text-left w-24">Status</th>
                        <th className="px-4 py-2 text-left">Event / Order</th>
                        <th className="px-4 py-2 text-left">Reason / License</th>
                        <th className="px-4 py-2 w-8"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/70">
                      {deliveries.map((d) => {
                        const isExpanded = expanded === d.id;
                        return (
                          <React.Fragment key={d.id}>
                            <tr
                              data-testid={`webhook-delivery-row-${d.id}`}
                              className={`cursor-pointer ${d.status === 'accepted' ? 'hover:bg-slate-800/30' : d.status === 'duplicate' ? 'bg-amber-500/5 hover:bg-amber-500/10' : 'bg-red-500/5 hover:bg-red-500/10'}`}
                              onClick={() => setExpanded(isExpanded ? null : d.id)}
                            >
                              <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{fmtTime(d.received_at)}</td>
                              <td className="px-4 py-2.5">
                                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${STATUS_CLS[d.status] || 'bg-slate-700 text-slate-300'}`}>
                                  {d.status}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-xs min-w-0">
                                <div className="font-mono text-slate-300 truncate">{d.event_id || '—'}</div>
                                <div className="text-[11px] text-slate-500 truncate">{d.order_id || '—'}</div>
                              </td>
                              <td className="px-4 py-2.5 text-xs min-w-0">
                                {d.license_id ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-300">
                                    <CheckCircle2 className="w-3 h-3" /> <span className="font-mono truncate">{d.license_id}</span>
                                  </span>
                                ) : (
                                  <span className="text-slate-400 truncate">{d.reason || '—'}</span>
                                )}
                              </td>
                              <td className="px-2 py-2.5 text-slate-500">
                                <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="bg-slate-950/40" data-testid={`webhook-delivery-details-${d.id}`}>
                                <td colSpan={5} className="px-6 py-3 space-y-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                    {d.reason && <div className="flex gap-2"><span className="text-slate-500 w-24">reason</span><span className="text-orange-300 font-mono">{d.reason}</span></div>}
                                    {d.license_id && <div className="flex gap-2"><span className="text-slate-500 w-24">license_id</span><span className="font-mono text-slate-300 truncate">{d.license_id}</span></div>}
                                    {d.signature_header && <div className="flex gap-2"><span className="text-slate-500 w-24">signature</span><span className="font-mono text-slate-400 truncate">{d.signature_header}</span></div>}
                                  </div>
                                  {d.payload_preview && (
                                    <div>
                                      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Payload preview</div>
                                      <pre className="bg-slate-950 border border-slate-800 rounded-md p-3 text-[11px] text-emerald-200 overflow-auto max-h-60">{d.payload_preview}</pre>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {total > 0 && (
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <div>Page {page} of {totalPages}</div>
                  <div className="flex gap-2">
                    <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || fetching} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Prev</Button>
                    <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || fetching} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Next</Button>
                  </div>
                </div>
              )}
            </section>
          </>
        )}
      </main>
      {otpDialog}
    </div>
  );
}
